/**
 * Data-driven product category definitions.
 *
 * Replaces the hardcoded inline-IIFE filters that were spread across the
 * landing page JSX. Adding a new product category or rearranging items now
 * requires editing only this file.
 */

export interface ProductCategory {
  /** Unique key used for React keying */
  key: string;
  /** Display label */
  label: string;
  /** Tailwind colour token (used for badge bg, text, border, line gradient) */
  color: "indigo" | "rose" | "amber" | "purple" | "emerald" | "cyan";
  /** Animation delay class */
  animationDelay?: string;
  /**
   * Returns true if the given product belongs to this category.
   * Receives the product object from the API.
   */
  match: (product: {
    id: string;
    type?: string;
    isHidden?: boolean;
    category?: string | null;
    name?: string;
  }) => boolean;
  /** If true, the category only shows after player validation */
  requiresValidation?: boolean;
  /** If true, items use channel-item cart keys (prefixed with `ch_`) */
  isChannelCategory?: boolean;
}

/** Diamond product IDs (the set that was previously hardcoded in three places). */
export const DIAMOND_IDS = [
  "25",
  "100",
  "310",
  "520",
  "1060",
  "2180",
  "5600",
  "11500",
] as const;

/** ID-specific diamond products (kiosgamer) */
export const ID_DIAMOND_IDS = [
  "5",
  "50",
  "70",
  "140",
  "355",
  "720",
  "7290",
] as const;

export const SUBSCRIPTION_IDS = [
  "weekly",
  "weekly max",
  "monthly",
  "lite",
  "vip",
  "svip",
  "id_weekly",
  "id_monthly",
  "id_bp_card",
  "gh-free-fire-tw-bp",
  "gh-free-fire-tw-mc",
  "gh-free-fire-tw-wc",
  "gh-free-fire-tw-wl",
] as const;
export const ID_SUBSCRIPTION_IDS = [
  "weekly",
  "monthly",
  "lite",
  "id_weekly",
  "id_monthly",
  "id_bp_card",
] as const;
export const EVO_IDS = ["3d", "7d", "30d"] as const;

export const isOffer = (p: {
  id?: string;
  name?: string;
  category?: string | null;
}) => {
  if (!p) return false;
  const cat = (p.category || "").trim().toLowerCase();
  const idLower = (p.id || "").toLowerCase();
  const nameLower = (p.name || "").toLowerCase();
  return (
    cat === "offers" ||
    cat === "offer" ||
    cat === "one_time" ||
    cat === "onetime" ||
    cat === "one-time" ||
    cat === "special_offers" ||
    cat === "special-offers" ||
    idLower.startsWith("offer_") ||
    idLower.startsWith("offer-") ||
    idLower.startsWith("onetime_") ||
    idLower.startsWith("one_time_") ||
    nameLower.startsWith("one time") ||
    nameLower.startsWith("special offer")
  );
};

export const isLevelUp = (p: {
  id: string;
  name?: string;
  category?: string | null;
}) => {
  const cat = (p.category || "").trim().toLowerCase();
  if (cat === "level_ups" || cat === "level_up" || cat === "levelup")
    return true;
  const idLower = p.id.toLowerCase();
  const nameLower = (p.name || "").toLowerCase();
  return (
    idLower.startsWith("lvl_") ||
    idLower.startsWith("id_lvl_") ||
    idLower.includes("lup") ||
    idLower.includes("level-up") ||
    nameLower.includes("level up") ||
    nameLower.includes("level-up")
  );
};

export const isEvoAccess = (p: {
  id: string;
  name?: string;
  category?: string | null;
}) => {
  const cat = (p.category || "").trim().toLowerCase();
  if (cat === "evos" || cat === "evo" || cat === "evo_access") return true;
  const idLower = p.id.toLowerCase();
  const nameLower = (p.name || "").toLowerCase();
  return (
    idLower.includes("evo") ||
    nameLower.includes("evo access") ||
    ["3d", "7d", "30d", "60d"].includes(idLower)
  );
};

export const isSubscription = (p: {
  id: string;
  name?: string;
  category?: string | null;
  type?: string;
}) => {
  const idLower = p.id.trim().toLowerCase();
  const nameLower = (p.name || "").trim().toLowerCase();
  const category = (p.category || "").trim().toLowerCase();
  return (
    (SUBSCRIPTION_IDS as readonly string[]).includes(idLower) ||
    p.type === "BUNDLE" ||
    ["memberships", "subscriptions", "membership"].includes(category) ||
    nameLower.includes("membership") ||
    nameLower.includes("weekly") ||
    nameLower.includes("monthly") ||
    nameLower.includes("booyah pass") ||
    nameLower.includes("weekly max") ||
    nameLower === "vip" ||
    nameLower === "svip"
  );
};

export const isDiamond = (p: {
  id: string;
  name?: string;
  category?: string | null;
}) => {
  if (isSubscription(p)) return false;
  const cat = (p.category || "").trim().toLowerCase();
  if (cat === "diamonds" || cat === "diamond") return true;
  const idLower = p.id.toLowerCase();
  const nameLower = (p.name || "").toLowerCase();
  return (
    (DIAMOND_IDS as readonly string[]).includes(p.id) ||
    (ID_DIAMOND_IDS as readonly string[]).includes(p.id) ||
    /^\d+$/.test(idLower) ||
    idLower.includes("diamond") ||
    nameLower.includes("diamond")
  );
};

export const PRODUCT_CATEGORIES: ProductCategory[] = [
  {
    key: "offers",
    label: "One Time Offer",
    color: "emerald",
    animationDelay: "delay-50",
    match: (p) => !p.isHidden && isOffer(p),
  },
  {
    key: "subscriptions",
    label: "Subscriptions & Bundles",
    color: "indigo",
    match: (p) =>
      !p.isHidden &&
      !isOffer(p) &&
      !isEvoAccess(p) &&
      !isDiamond(p) &&
      !isLevelUp(p) &&
      isSubscription(p),
  },
  {
    key: "diamonds",
    label: "Diamonds",
    color: "rose",
    animationDelay: "delay-75",
    match: (p) => !p.isHidden && !isOffer(p) && isDiamond(p),
  },
  {
    key: "evos",
    label: "Evo Access",
    color: "amber",
    animationDelay: "delay-150",
    match: (p) => !p.isHidden && !isOffer(p) && isEvoAccess(p),
  },
  {
    key: "level_ups",
    label: "Level Up Pass",
    color: "purple",
    animationDelay: "delay-200",
    match: (p) => !p.isHidden && !isOffer(p) && isLevelUp(p),
  },
];

/** Colour-token → Tailwind class mapping for category badges. */
export const CATEGORY_STYLES: Record<
  string,
  { text: string; bg: string; border: string; gradient: string }
> = {
  emerald: {
    text: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    border: "border-emerald-100/50 dark:border-emerald-800/50",
    gradient: "from-emerald-100 dark:from-emerald-950/20",
  },
  cyan: {
    text: "text-cyan-600 dark:text-cyan-400",
    bg: "bg-cyan-50 dark:bg-cyan-950/40",
    border: "border-cyan-100/50 dark:border-cyan-800/50",
    gradient: "from-cyan-100 dark:from-cyan-950/20",
  },
  indigo: {
    text: "text-indigo-600 dark:text-indigo-400",
    bg: "bg-indigo-50 dark:bg-indigo-950/40",
    border: "border-indigo-100/50 dark:border-indigo-800/50",
    gradient: "from-indigo-100 dark:from-indigo-950/20",
  },
  rose: {
    text: "text-rose-600 dark:text-rose-400",
    bg: "bg-rose-50 dark:bg-rose-950/40",
    border: "border-rose-100/50 dark:border-rose-800/50",
    gradient: "from-rose-100 dark:from-rose-950/20",
  },
  amber: {
    text: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-50 dark:bg-amber-950/40",
    border: "border-amber-100/50 dark:border-amber-800/50",
    gradient: "from-amber-100 dark:from-amber-950/20",
  },
  purple: {
    text: "text-purple-600 dark:text-purple-400",
    bg: "bg-purple-50 dark:bg-purple-950/40",
    border: "border-purple-100/50 dark:border-purple-800/50",
    gradient: "from-purple-100 dark:from-purple-950/20",
  },
};
